import React from 'react';
import { PiMagnifyingGlass } from 'react-icons/pi';
import type { TCAction } from 'src/api/generated';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import { ToolsGridSkeleton } from './tools-grid-skeleton';
import { Text } from 'src/modules/shared/components/atoms/text';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getToolMetadata } from 'src/modules/automations/utils/employees/employee-tool-presentation.util';

interface ToolSearchProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredTools: TCAction[];
  isLoading: boolean;
  onSelectTool: (tool: TCAction) => void;
  selectedTool: TCAction | null;
}

export const ToolSearch: React.FC<ToolSearchProps> = ({
  searchQuery,
  setSearchQuery,
  filteredTools,
  isLoading,
  onSelectTool,
  selectedTool,
}) => {
  const { t } = useI18n();

  return (
    <>
      <div className="relative mb-4">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <PiMagnifyingGlass className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          // TODO: check when implementing i18n
          placeholder="Busca una herramienta..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 p-2 border border-gray-300 rounded-md w-full"
        />
      </div>

      <div className="max-h-80 overflow-y-auto pr-2">
        <div className="grid grid-cols-1 gap-4">
          {isLoading ? (
            <ToolsGridSkeleton count={4} showHeaderAction={false} />
          ) : filteredTools.length === 0 ? (
            <div className="col-span-full">
              {/* TODO: check when implementing i18n */}
              <EmptyState
                title={
                  searchQuery
                    ? 'No se encontraron herramientas'
                    : 'No hay herramientas disponibles'
                }
                icon={<PiMagnifyingGlass />}
                placement="horizontal"
              />
            </div>
          ) : (
            filteredTools.map((tool) => (
              <Card
                key={tool.name}
                onClick={() => onSelectTool(tool)}
                size="sm"
                variant="secondary"
                isHighlighted={selectedTool?.name === tool.name}
                appearance="soft"
              >
                <CardBody className="p-2">
                  <Text size="paragraph" weight="medium" className="mb-1">
                    {
                      getToolMetadata(tool.name, t, tool.annotations?.title)
                        .displayName
                    }
                  </Text>
                  {tool.description && (
                    <Text size="small" color="neutral" className="line-clamp-2">
                      {tool.description}
                    </Text>
                  )}
                </CardBody>
              </Card>
            ))
          )}
        </div>
      </div>
    </>
  );
};
